import { FC, useEffect, useMemo, useState } from 'react';
import { Table, TableRow, TBody, TD, TH, THead } from '@/components/DesignLibrary/Table';
import Button from '@/components/DesignLibrary/Button';
import CollapsibleArea from '@/components/CollapsibleArea';
import { InternalDataSource } from '@/tsClient/servers/noodleDocuments/document-api-models';
import findArrayinJSON from '@/helpers/findArrayInJSON';
import formatObjectAsTable from '@/helpers/formatObjectsAsTable';
import { XCircle } from '@phosphor-icons/react/dist/ssr';
import CopyButton from '@/components/CopyButton';
import { Copy } from '@phosphor-icons/react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import Spacer from '@/components/Spacer';
import getDataReferenceVersionsBySlugs from '@/tsClient/questionnaires/getDataReferenceVersionsBySlugs';
import useNoodleApi from '@/hooks/useNoodleApi';
import { Questionnaire } from '@/typings/Questionnaire';
import { Select } from '@/components/DesignLibrary/Atoms';
import rerunAiAgentOnQuestionnaireField from '@/tsClient/questionnaires/rerunAiAgentOnQuestionnaireField';
import { useFormContext } from '@/context/FormContext';
import { useToast } from '@hooks';
import { isNoodler } from '@/providers/FeatureFlags/checkFeatureFlag';
import { useUserProfile } from '@providers/Auth';

import { IBM_Plex_Mono as ibmPlexMono } from 'next/font/google';
import NoodleAI from '@/components/Icons/NoodleAI';
import { getAiDetails } from '@/tsClient/ai';
import Shimmer from '@/components/Shimmer';
import s from './ConnectedDataPanel.module.scss';

const mono = ibmPlexMono({
  subsets: ['latin'],
  weight: '400',
});

type Props = {
  data?: {
    title: string;
    values: { property: string; value: string }[] | null;
    onEditClick?: () => void;
    onClose?: () => void;
    type?: 'autofill' | 'ai-autofill' | 'questionnaire';
  };
  field?: Questionnaire.FieldCommon;
  hideCloseIcon?: boolean;
};

interface JsonData {
  [key: string]: {
    label: string;
    value:
      | string
      | {
          [key: string]: string;
        };
  };
}

interface ExtractedData {
  label: string;
  value: string | { [key: string]: string };
}

function extractValuesByKey(jsonData: JsonData, sampleKey: string): ExtractedData[] {
  if (!sampleKey) {
    console.error('No key provided for extraction.');
    return [];
  }
  const match = sampleKey.match(/\[(\d+)\]/); // Extract the index from the given key
  const extractedData: ExtractedData[] = [];
  if (!match) {
    Object.entries(jsonData).forEach(([_key, { label, value }]) => {
      extractedData.push({ label, value });
    });
    return extractedData;
  }

  const targetIndex = parseInt(match[1], 10);

  Object.entries(jsonData).forEach(([key, { label, value }]) => {
    const keyMatch = key.match(/\[(\d+)\]/); // Extract index from current key
    if (keyMatch && parseInt(keyMatch[1], 10) === targetIndex) {
      extractedData.push({ label, value });
    }
  });

  return extractedData;
}

const AiAgentData: FC<Props> = ({ data, hideCloseIcon = false,field }): JSX.Element => {
  const { setValue, questionnaireId, getAiRequestId, fieldsByKey } = useFormContext();
  const [aiRequestIdLocal,setAiRequestIdLocal] = useState<string | null>();
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [isLoadingAiDetails, setIsLoadingAiDetails] = useState(false);
  const { data: dataRefVersions, getData: getDataReferenceVersionsBySlugsFn } = useNoodleApi(getDataReferenceVersionsBySlugs);
  const {data: aiDetails, getData: getAiDetailsFn } = useNoodleApi(getAiDetails);
  const { getData: rerunAiAgentFn } = useNoodleApi(rerunAiAgentOnQuestionnaireField, {
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Successfully re-ran ai agent with new data sets'],
  });

  const externalData: { [key: string]: { data: string; label: string; slug: string } } = useMemo(
    () => JSON.parse((data?.values || []).find(({ property }) => property === 'externalData')?.value || '{}'),
    [data?.values],
  );
  const externalDataSlugs = useMemo(() => Object.keys(externalData).map(key => externalData[key].slug), [externalData]);
  const profile = useUserProfile();
  const [builtResponse,setBuiltResponse] = useState('');
  const [externalDataKeys, setExternalDataKeys] = useState(Object.keys(externalData));

  const fieldInputs: {
    [key: string]: {
      label: string;
      value: string | { [key: string]: string };
    };
  } = JSON.parse((data?.values || []).find(({ property }) => property === 'fieldInputs')?.value || '{}');

  const internalData: Array<Pick<InternalDataSource, 'id' | 'referenceId' | 'referenceType' | 'internalData'>> = JSON.parse(
    (data?.values || []).find(({ property }) => property === 'internalData')?.value || '[]',
  );

  const fieldKey = (data?.values || []).find(({ property }) => property === 'fieldKey')?.value;

  const extractedInputs = extractValuesByKey(fieldInputs, fieldKey || '');

  const expression = (data?.values || []).find(({ property }) => property === 'expression')?.value;

  const description = (data?.values || []).find(({ property }) => property === 'description')?.value;

  const variables: {
    variableName: string;
    referenceId: string;
    referenceType: string;
    selector: string;
  }[] = JSON.parse((data?.values || []).find(({ property }) => property === 'variables')?.value || '[]');

  const fieldKeys = useMemo(() => {
    if(fieldKey) {
      return field?.linkedFieldKeys?.length
        ? [...field.linkedFieldKeys, fieldKey]
        : [fieldKey];
    }
    return [];
  }, [field?.linkedFieldKeys, fieldKey]);

  useEffect(() => {
    setExternalDataKeys(Object.keys(externalData));
  }, [externalData]);

  useEffect(() => {
    const fetchedAiRequestId = getAiRequestId(fieldKey || '');
    setAiRequestIdLocal(fetchedAiRequestId);
  },[fieldKey,getAiRequestId]);

  useEffect(() => {
    if (!aiRequestIdLocal) return;

    const fetchAiDetails = async ():Promise<void> => {
      try{
        setIsLoadingAiDetails(true);
        await getAiDetailsFn({
          aiRequestId: aiRequestIdLocal,
        });
      }
      catch(error) {
        console.error('Error fetching Ai details: ',error);
      }
    };

    fetchAiDetails();
  },[aiRequestIdLocal]);

  const responseProvider = (responseFromAi: string, fieldKeysLocal: string[]): string => {
    try {
      const parsedData = JSON.parse(responseFromAi || '{}');
      for(let i=0; i<fieldKeysLocal.length; i+=1) {
        if(fieldKeysLocal[i] && parsedData[fieldKeysLocal[i]]) {
          return parsedData[fieldKeysLocal[i]];
        }
      }
      return '';
    } catch (error) {
      return responseFromAi || '';
    }
  };

  useEffect(() => {
    if(!fieldKeys.length || !aiDetails?.response) return;
    setBuiltResponse(responseProvider(aiDetails?.response || '',fieldKeys));
  },[aiDetails?.response,fieldKeys]);

  useEffect(() => {
    if(isLoadingAiDetails) {
      setIsLoadingAiDetails(false);
    }
  },[aiDetails]);

  useEffect(() => {
    const fetchVersions = async (): Promise<void> => {
      if (externalDataSlugs.length === 0) return;

      try {
        await getDataReferenceVersionsBySlugsFn({ slugs: externalDataSlugs });
      } catch (error) {
        console.error('Error fetching versions: ', error);
      }
    };

    fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalDataSlugs.length]);

  const handleSelectChange = (index: number, selectedVersion: string): void => {
    setExternalDataKeys(prevKeys => {
      const updatedKeys = [...prevKeys];
      updatedKeys[index] = selectedVersion;

      const rerunAgent = async (): Promise<void> => {
        if (questionnaireId && fieldKey) {
          try {
            const result = await rerunAiAgentFn({
              externalDataSetIds: updatedKeys,
              fieldKey,
              questionnaireId,
            });

            const aiAgentAnswers = result.data;

            if (aiAgentAnswers) {
              Object.entries(aiAgentAnswers).forEach(([key, value]) => {
                setValue({ aiRequestId:aiAgentAnswers?.aiRequestId, key, source: 'ai' , value });
                const splitKey = key.split('].');
                let keyBuilt = key;
                if(splitKey.length > 1) {
                  [,keyBuilt] = splitKey;
                }
                if(key === keyBuilt) {
                  const fieldsFetched = fieldsByKey[keyBuilt];
                  fieldsFetched?.linkedFieldKeys?.forEach((linkedFieldKey) => {
                    setValue({ aiRequestId:aiAgentAnswers?.aiRequestId, key:linkedFieldKey, source: 'ai' , value });
                  });
                }
              });
            }
          } catch (error) {
            console.error('Error rerunning agent: ', error);
          }
        }
      };

      rerunAgent();
      return updatedKeys;
    });
  };

  return (
    <>
      {data?.onClose && (
        <Button variant="editable" size="xs" onClick={data.onClose} className={s.closeButton}>
          {!hideCloseIcon && <XCircle size={24} weight="fill" color="var(--color-gray-100)" />}
        </Button>
      )}
      <div className={s.container}>
        <>
          {isNoodler(profile) && <CollapsibleArea open title='Ai Details' CustomIcon={<NoodleAI size={24} />}>
            {aiDetails
              ? <div className={s.aboutAiCard}>
                <div className={s.aboutAiLineItems}>
                  <div className={s.aboutAiProperty}>Model</div>
                  <div className={s.aboutAiValue}>{aiDetails?.aiModel || '---'}</div>
                </div>
                <div className={s.aboutAiLineItems}>
                  <div className={s.aboutAiProperty}>Data sent</div>
                  <div className={s.aboutAiValue}>{aiDetails?.dataSent || '---'}</div>
                </div>
                <div className={s.aboutAiLineItems}>
                  <div className={s.aboutAiProperty}>Prompt</div>
                  <div className={s.aboutAiValue}>
                    <span>{aiDetails
                      ? (showFullPrompt
                        ? (aiDetails?.prompt?.split('\n')?.map((line,index) => <div key={index}>{line}</div>)) || '---'
                        : `${aiDetails?.prompt?.slice(0,200)}...`)
                      : '---'}
                    </span>
                    <span className={s.longPrompt} onClick={() => {
                      setShowFullPrompt(!showFullPrompt);
                    }}>{!!aiDetails?.prompt && (showFullPrompt ? 'View less' : 'View more')}</span>
                  </div>
                </div>
                <div className={s.aboutAiLineItems}>
                  <div className={s.aboutAiProperty}>Response</div>
                  <div className={s.aboutAiValue}>{builtResponse}</div>
                </div>
              </div>
              : !isLoadingAiDetails
                ? <div className={s.loading}>No Data</div>
                : <div className={s.loading}>
                  <Shimmer message='Loading...' />
                </div>}
          </CollapsibleArea>}
        </>
        {Object.keys(externalData).length > 0 && (
          <>
            <h2 className='body-sm-bold'>External Data</h2>
            <div className={s.card}>
              {Object.entries(externalData).map(([key, { data: jsonData, label, slug }], index) => {
                const externalDataVersions = dataRefVersions?.filter(ref => ref.slug === slug);
                let parsedData;
                try {
                  parsedData = JSON.parse(jsonData);
                } catch (error) {
                  console.error('Error parsing externalData json', error);
                  return null;
                }

                let tableData = findArrayinJSON(parsedData);

                if (!tableData && typeof parsedData === 'object' && parsedData !== null) {
                  tableData = formatObjectAsTable(parsedData);
                }

                if (parsedData.states) {
                  return (
                    <div key={label}>
                      <h2 className="body-md-bold" style={{ padding: 'var(--padding-df)' }}>
                        {label}
                      </h2>
                      <div className={s.stateVersionSelect}>
                        {externalDataVersions && externalDataVersions.length > 0 && (
                          <Select
                            compact
                            label=""
                            placeholder="Select Version"
                            options={externalDataVersions.map(v => ({
                              description: `Version - ${v.version}`,
                              key: v.id,
                              label: v.versionLabel,
                            }))}
                            onChange={selectedVersion => handleSelectChange(index, selectedVersion)}
                            value={externalDataKeys[index]}
                          />
                        )}
                      </div>
                      {Object.entries(parsedData.states).map(([stateName, stateData]) => {
                        const subTableData = findArrayinJSON(stateData);
                        return (
                          <CollapsibleArea description={stateName} key={stateName} title="">
                            {subTableData && (
                              <Table>
                                <THead>
                                  <TableRow>
                                    {Object.keys(subTableData[0] || {}).map(header => (
                                      <TH key={header} label={header} />
                                    ))}
                                  </TableRow>
                                </THead>
                                <TBody>
                                  {subTableData.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                      {(typeof row === 'object' && row !== null ? Object.values(row) : []).map((rowValue, colIndex) => (
                                        <TD key={colIndex}>
                                          <CopyButton isWrapper className={s.copyButton} text={String(rowValue)} title="Click to copy">
                                            {String(rowValue)}
                                          </CopyButton>
                                        </TD>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TBody>
                              </Table>
                            )}
                          </CollapsibleArea>
                        );
                      })}
                      <Spacer />
                    </div>
                  );
                }
                if (slug === 'state-exemptions-v0.1' && parsedData.table) {
                  // Group rows by state
                  type Exemption = typeof parsedData.table[number];

                  const groupedByState = parsedData.table.reduce((acc: Record<string, Exemption[]>, row: Exemption) => {
                    if (!acc[row.state]) {
                      acc[row.state] = [];
                    }
                    acc[row.state].push(row);
                    return acc;
                  }, {} as Record<string, Exemption[]>);

                  return (
                    <div key={label}>
                      <h2 className="body-md-bold" style={{ padding: 'var(--padding-df)' }}>
                        {label}
                      </h2>
                      <div className={s.stateVersionSelect} style={{ padding: '0 var(--padding-df)' }}>
                        {externalDataVersions && externalDataVersions.length > 0 && (
                          <Select
                            compact
                            label=""
                            placeholder="Select Version"
                            options={externalDataVersions.map(v => ({
                              description: `Version - ${v.version}`,
                              key: v.id,
                              label: v.versionLabel,
                            }))}
                            onChange={selectedVersion => handleSelectChange(index, selectedVersion)}
                            value={externalDataKeys[index]}
                          />
                        )}
                      </div>

                      {Object.entries(groupedByState).map((entry) => {
                        const [stateName, stateRows] = entry as [string, Exemption[]];

                        return (
                          <CollapsibleArea description={Object.entries(groupedByState).length > 1 ? stateName : ''} key={stateName} title={Object.entries(groupedByState).length > 1 ? '' : stateName}>
                            {stateRows.length > 0 && (
                              <Table>
                                <THead>
                                  <TableRow>
                                    {Object.keys(stateRows[0] || {})
                                      .filter(header => header !== 'state' && header !== 'stateOfFilingFilter')
                                      .map(header => (
                                        <TH key={header} label={header} />
                                      ))}
                                  </TableRow>
                                </THead>
                                <TBody>
                                  {stateRows.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                      {Object.entries(row)
                                        .filter(([rowKey]) => rowKey !== 'state' && rowKey !== 'stateOfFilingFilter')
                                        .map(([_, value], colIndex) => (
                                          <TD key={colIndex}>
                                            <CopyButton isWrapper className={s.copyButton} text={String(value)} title="Click to copy">
                                              {String(value)}
                                            </CopyButton>
                                          </TD>
                                        ))}
                                    </TableRow>
                                  ))}
                                </TBody>
                              </Table>
                            )}
                          </CollapsibleArea>
                        );
                      })}
                      {Object.entries(groupedByState).length > 1 && <Spacer />}
                    </div>
                  );
                }
                if (!tableData) {
                  return (
                    <CollapsibleArea title={label} key={label}>
                      <p className="body-sm" style={{ padding: 'var(--padding-df)' }}>
                        No data available.
                      </p>
                    </CollapsibleArea>
                  );
                }
                return (
                  <>
                    <CollapsibleArea
                      title={label}
                      description={externalDataVersions?.find(v => v.id === externalDataKeys[index])?.versionLabel || ''}
                      key={key}
                    >
                      {externalDataVersions && externalDataVersions.length > 1
                        ? (
                          <div style={{ padding: '16px 16px 8px 16px' }}>
                            <Select
                              compact
                              optional
                              label="Select version"
                              placeholder="Select version"
                              options={externalDataVersions.map(v => ({
                                key: v.id,
                                label: `${v.versionLabel} (${v.version})`,
                              }))}
                              onChange={selectedVersion => handleSelectChange(index, selectedVersion)}
                              value={externalDataKeys[index]}
                            />
                          </div>
                        )
                        : undefined}
                      <Table>
                        <THead>
                          <TableRow>
                            {Object.keys(tableData[0] || {}).map(tableKey => (
                              <TH key={tableKey} label={tableKey} />
                            ))}
                          </TableRow>
                        </THead>
                        <TBody>
                          {tableData.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                              {(typeof row === 'object' && row !== null ? Object.values(row) : []).map((value, colIndex) => (
                                <TD key={colIndex}>
                                  <CopyButton isWrapper className={s.copyButton} text={String(value)} title="Click to copy">
                                    {String(value)}
                                  </CopyButton>
                                </TD>
                              ))}
                            </TableRow>
                          ))}
                        </TBody>
                      </Table>
                    </CollapsibleArea>
                  </>
                );
              })}
            </div>
          </>
        )}
        {internalData.length > 0 && (
          <>
            <h2 className='body-sm-bold'>Internal Data</h2>
            <div className={s.card}>
              {internalData.map(({ internalData: internalDataRecord }: Pick<InternalDataSource, 'internalData'>, index) => (
                // show data.specificType (data.type) as header eg: Driver License (personal)
                <div key={index}>
                  <CollapsibleArea
                    title={`${
                      (internalDataRecord.data as Record<string, string>)?.specificType
                        ? (internalDataRecord.data as Record<string, string>).specificType
                        : 'Internal Data'
                    }
              (${
                (internalDataRecord.data as Record<string, string>)?.type
                  ? (internalDataRecord.data as Record<string, string>).type
                  : 'type'
                })`}
                  >
                    <Table>
                      <THead>
                        <TableRow>
                          <TH label="Property" />
                          <TH label="Value" />
                        </TableRow>
                      </THead>
                      <TBody>
                        {(internalDataRecord?.data as Record<string, unknown>)?.fields && (
                          <>
                            {Object.entries((internalDataRecord.data as Record<string, Record<string, string>>).fields).map(([key, value]) => (
                              <TableRow key={key}>
                                <TD compact label={key} />
                                <TD compact label={value} />
                              </TableRow>
                            ))}
                          </>
                        )}
                      </TBody>
                    </Table>
                  </CollapsibleArea>
                </div>
              ))}
            </div>
          </>
        )}
        {extractedInputs.length > 0 && (
          <>
            <h2 className='body-sm-bold'>Inputs</h2>
            <div className={s.card}>
              <>
                {data?.title && <p className="body-md-bold">{data?.title}</p>}
                <Table>
                  <THead>
                    <TableRow>
                      <TH label="Property" />
                      <TH label="Value" />
                    </TableRow>
                  </THead>
                  <TBody>
                    {extractedInputs.map(({ label, value }) => (
                      <TableRow key={label}>
                        <TD compact label={label} />
                        <TD compact label={typeof value === 'string' ? value : JSON.stringify(value)} />
                      </TableRow>
                    ))}
                  </TBody>
                </Table>
              </>
            </div>
          </>
        )}
        {(description || expression || variables.length > 0) && <>
          <h2 className='body-sm-bold'>Expression</h2>
          {description && (
            <>
              <div className={s.card} style={{ padding: 'var(--padding-df' }}>
                <p className="body-sm">{description}</p>
              </div>
            </>
          )}
          {expression && (
            <div className={s.card}>
              <SyntaxHighlighter
                language="javascript"
                customStyle={{
                  backgroundColor: 'var(--color-gray-0)',
                  fontSize: 11,
                  margin: 0,
                  padding: '6px 8px',
                }}
                wrapLongLines
                codeTagProps={{ style: mono.style }}
              >
                {expression}
              </SyntaxHighlighter>
            </div>
          )}
          {variables.length > 0 && (
            <div className={s.card}>
              <Table>
                <THead>
                  <TableRow>
                    <TH label="Variable Name" />
                    <TH label="Reference ID" />
                    <TH label="Selector" />
                    <TH label="Reference Type" />
                  </TableRow>
                </THead>
                <TBody>
                  {variables.map(({ variableName, referenceType, referenceId, selector }) => (
                    <TableRow key={variableName}>
                      <TD>
                        <CopyButton text={variableName} className={s.copyVar}>
                          <Copy size={12} />
                          {variableName}
                        </CopyButton>
                      </TD>
                      <TD label={referenceId || '—'} compact />
                      <TD label={selector || '—'} compact />
                      <TD label={referenceType || '—'} compact />
                    </TableRow>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </>}
      </div>
    </>
  );
};

export default AiAgentData;
